import { dataTypeSymbol } from "../../../logged-in/shared/functions/Scorecard"
import { departmentSignatureTable } from "../../../logged-in/shared/functions/scorecard-pdf/DocDefition"
import { dateFormat } from "../../../logged-in/shared/utils/utils"
import { fullPerspectiveName } from "../../interfaces/IPerspectiveTabs"
import { IMeasureDepartment } from "../../models/MeasureDepartment"
import { IObjective } from "../../models/Objective"
import { dataFormat } from "../Directives"
import { marginTopBottom, header, sectionHeader } from "./DocDefition"
import { brandLogo } from "./ImageDefinition"

// Simplified table structure to avoid complexity issues
const tableWidths = [
  80, // Perspective
  120, // Strategic Objectives
  120, // Contributory Objective
  40, // Weight
  100, // Measures/KPI
  60, // Baseline
  60, // Q1
  60, // Q2
  60, // Q3
  60, // Q4
  60, // Annual
  80, // Rating Scale
  80, // Initiatives
  60, // Target Date
  80, // Evidence
  80, // Comments
]

const tableHeader = [
  { text: "Perspective", style: "tableHeader" },
  { text: "Strategic Objectives", style: "tableHeader" },
  { text: "Contributory Objective", style: "tableHeader" },
  { text: "Weight (%)", style: "tableHeader" },
  { text: "Measures/KPI", style: "tableHeader" },
  { text: "Baseline", style: "tableHeader" },
  { text: "Q1 Target", style: "tableHeader" },
  { text: "Q2 Target", style: "tableHeader" },
  { text: "Q3 Target", style: "tableHeader" },
  { text: "Q4 Target", style: "tableHeader" },
  { text: "Annual Target", style: "tableHeader" },
  { text: "Rating Scale", style: "tableHeader" },
  { text: "Key Initiatives", style: "tableHeader" },
  { text: "Target Date", style: "tableHeader" },
  { text: "Evidence", style: "tableHeader" },
  { text: "Comments", style: "tableHeader" },
]

export interface IDepartmentAgreementTableRowItem {
  perspective: string
  strategicObjective: string
  contributoryObjective: string
  weight: number
  measure: string | number
  baseline: string | number
  q1target: string | number
  q2target: string | number
  q3target: string | number
  q4target: string | number
  annualTarget: string | number
  ratingScale: string
  keyInitiatives: string | number
  targetDate: string | number
  sourceOfEvidence: string
  comments: string
  dataType?: string
  dataSymbol?: string
}

const ConvertToTableRowItem = (
  strategicObjectives: IObjective[],
  contributoryObjectives: IObjective[],
  measures: IMeasureDepartment[],
): IDepartmentAgreementTableRowItem[] => {
  const sortByPerspective = (a: IDepartmentAgreementTableRowItem, b: IDepartmentAgreementTableRowItem) => {
    const order = ["F", "C", "I", "L"]
    const aIndex = order.indexOf(a.perspective.charAt(0))
    const bIndex = order.indexOf(b.perspective.charAt(0))
    return (
      aIndex - bIndex ||
      a.strategicObjective.localeCompare(b.strategicObjective) ||
      a.contributoryObjective.localeCompare(b.contributoryObjective)
    )
  }

  const formatMeasureValue = (type: string, value: string | number | null) => {
    if (!value && value !== 0) return ""

    const symbol = dataTypeSymbol(type)
    const suffix = symbol.suffix || ""
    const prefix = symbol.prefix || ""

    if (type === "Date") return dateFormat(Number(value))
    if (type === "Currency") return `${prefix}${Number(value).toFixed(2)}${suffix}`
    return `${prefix}${value}${suffix}`
  }

  const tableRows: IDepartmentAgreementTableRowItem[] = measures.map((m) => {
    // Get contributory objective
    const contributory = contributoryObjectives.find((o) => o.id === m.objective)

    // Get strategic objective
    const strategic = strategicObjectives.find((s) => {
      if (!contributory) return false
      return s.id === contributory.parent
    })

    const ratingScale = () => {
      const ratings = [
        m.rating1 ? `1 = ${dataFormat(m.dataType, m.rating1, m.dataSymbol)}` : "1 = 0",
        m.rating2 ? `2 = ${dataFormat(m.dataType, m.rating2, m.dataSymbol)}` : "2 = 0",
        m.rating3 ? `3 = ${dataFormat(m.dataType, m.rating3, m.dataSymbol)}` : "3 = 0",
        m.rating4 ? `4 = ${dataFormat(m.dataType, m.rating4, m.dataSymbol)}` : "4 = 0",
        m.rating5 ? `5 = ${dataFormat(m.dataType, m.rating5, m.dataSymbol)}` : "5 = 0",
      ]
      return ratings.join("\n")
    }

    const row: IDepartmentAgreementTableRowItem = {
      perspective: fullPerspectiveName(contributory ? contributory.perspective : ""),
      strategicObjective: strategic ? strategic.description : "Unknown",
      contributoryObjective: contributory ? contributory.description : "Unknown",
      weight: contributory ? contributory.weight || 0 : 0,
      measure: m ? m.description : "Unknown",
      baseline: m ? formatMeasureValue(m.dataType, m.baseline) : "",
      q1target: m ? formatMeasureValue(m.dataType, m.quarter1Target) : "",
      q2target: m ? formatMeasureValue(m.dataType, m.quarter2Target) : "",
      q3target: m ? formatMeasureValue(m.dataType, m.quarter3Target) : "",
      q4target: m ? formatMeasureValue(m.dataType, m.quarter4Target) : "",
      annualTarget: m ? formatMeasureValue(m.dataType, m.annualTarget) : "",
      ratingScale: ratingScale(),
      keyInitiatives: m ? m.activities || "" : "",
      targetDate: m ? m.targetDate || "" : "",
      sourceOfEvidence: m ? m.sourceOfEvidence || "" : "",
      comments: m ? m.comments || "" : "",
      dataType: m.dataType,
      dataSymbol: m.dataSymbol,
    }

    return row
  })

  return tableRows.sort(sortByPerspective)
}

// Simplified table formatting to avoid complex rowSpan issues
const FormatTableRows = (rows: IDepartmentAgreementTableRowItem[]) => {
  return rows.map((row) => [
    { text: row.perspective, fontSize: 9 },
    { text: row.strategicObjective, fontSize: 9 },
    { text: row.contributoryObjective, fontSize: 9 },
    { text: row.weight.toString(), fontSize: 9, alignment: "center" },
    { text: row.measure.toString(), fontSize: 9 },
    { text: row.baseline.toString(), fontSize: 9 },
    { text: row.q1target.toString(), fontSize: 9 },
    { text: row.q2target.toString(), fontSize: 9 },
    { text: row.q3target.toString(), fontSize: 9 },
    { text: row.q4target.toString(), fontSize: 9 },
    { text: row.annualTarget.toString(), fontSize: 9 },
    { text: row.ratingScale, fontSize: 8 },
    { text: row.keyInitiatives.toString(), fontSize: 9 },
    { text: row.targetDate.toString(), fontSize: 9 },
    { text: row.sourceOfEvidence, fontSize: 9 },
    { text: row.comments, fontSize: 9 },
  ])
}

export const DepartmentPerformanceAgreementDocument = async (
  title: string,
  vision: string,
  mission: string,
  strategicObjectives: IObjective[],
  contributoryObjectives: IObjective[],
  measures: IMeasureDepartment[],
) => {
  try {
    const logo = await brandLogo()

    const rows: IDepartmentAgreementTableRowItem[] = ConvertToTableRowItem(
      strategicObjectives,
      contributoryObjectives,
      measures,
    )

    const mappedRows = FormatTableRows(rows)
    const body = [tableHeader, ...mappedRows]

    return {
      pageSize: "A2",
      pageOrientation: "landscape",
      content: [
        logo,
        marginTopBottom(),
        header("Unicomms Strategy Execution and Performance Management System"),
        header(`${title}`),
        sectionHeader("_".repeat(200)),
        sectionHeader(`${new Date().toLocaleDateString()}`),
        marginTopBottom(),

        sectionHeader("Mission:"),
        sectionHeader(mission || "Mission statement not provided"),
        marginTopBottom(),

        sectionHeader("Vision:"),
        sectionHeader(vision || "Vision statement not provided"),
        marginTopBottom(),
        marginTopBottom(),

        header("PART A: Department Details"),
        sectionHeader("_".repeat(200)),
        marginTopBottom(),
        sectionHeader(`Department: ${title}`),
        marginTopBottom(),

        sectionHeader("_".repeat(200)),
        sectionHeader("Commitment: We set targets and strive towards achievements in unison."),
        marginTopBottom(),
        sectionHeader(
          "Professionalism: We deliver high-quality standard of service and demonstrate integrity and ethical behavior in carrying out our duties.",
        ),
        marginTopBottom(),
        sectionHeader(
          "Innovation: We are open to new ideas and continuously find efficient and effective ways in delivering services.",
        ),
        marginTopBottom(),
        sectionHeader("Transparency: We carry out duties in an open, fair, and procedural manner."),
        marginTopBottom(),
        sectionHeader("Accountability: We are responsible and answerable for our actions."),
        marginTopBottom(),
        marginTopBottom(),

        header("PART C: Scorecard"),
        sectionHeader("_".repeat(200)),
        marginTopBottom(),

        {
          table: {
            headerRows: 1,
            widths: tableWidths,
            body: body,
          },
          layout: {
            fillColor: (rowIndex: number) => (rowIndex === 0 ? "#3498db" : null),
          },
        },
        marginTopBottom(),
        marginTopBottom(),
        departmentSignatureTable(),
      ],
      styles: {
        tableHeader: {
          bold: true,
          fontSize: 10,
          color: "white",
          alignment: "center",
        },
      },
    }
  } catch (error) {
    console.error("Error creating department document:", error)
    throw error
  }
}
